interface ManifestData {
  version?: string;
  version_name?: string;
  [key: string]: any;
}

const STORAGE_KEY = 'app_build_version';
let isChecking = false;

/**
 * Xóa toàn bộ HTTP cache / Cache Storage và tải lại trang
 */
export async function clearCacheAndReload(newVersionSignature?: string) {
  try {
    // 1. Cập nhật version mới vào localStorage ngay lập tức để tránh reload lặp lại
    if (newVersionSignature) {
      localStorage.setItem(STORAGE_KEY, newVersionSignature);
    }

    // 2. Xóa Cache Storage của trình duyệt (PWA / Cache API)
    if ('caches' in window) {
      const cacheNames = await caches.keys();
      await Promise.all(cacheNames.map((name) => caches.delete(name)));
    }
  } catch (error) {
    console.error('[VersionCheck] Lỗi khi dọn dẹp cache:', error);
  }

  // 3. Buộc tải lại nội dung index.html mới nhất bằng timestamp để vượt qua HTTP cache của trình duyệt
  try {
    const url = new URL(window.location.href);
    url.searchParams.set('_reload', Date.now().toString());
    window.location.href = url.toString();
  } catch {
    window.location.reload();
  }
}

/**
 * Kiểm tra version từ file public/manifest.json
 */
export async function checkAppVersion(): Promise<boolean> {
  if (isChecking) return false;
  isChecking = true;

  try {
    const publicUrl = process.env.PUBLIC_URL || '';
    const manifestUrl = `${publicUrl}/manifest.json?t=${Date.now()}`;

    const res = await fetch(manifestUrl, {
      cache: 'no-store',
      headers: {
        'Cache-Control': 'no-cache, no-store, must-revalidate',
        Pragma: 'no-cache',
        Expires: '0',
      },
    });

    if (!res.ok) {
      isChecking = false;
      return false;
    }

    const data: ManifestData = await res.json();
    // Lấy version hoặc version_name từ manifest.json
    const remoteVersion = data.version_name || data.version;
    // Kết hợp cả 2 để nếu thay đổi version hoặc version_name đều phát hiện được
    const versionSignature = `${data.version || ''}_${data.version_name || ''}`;

    if (!versionSignature || versionSignature === '_') {
      isChecking = false;
      return false;
    }

    const currentVersionSignature = localStorage.getItem(STORAGE_KEY);

    if (!currentVersionSignature) {
      // Lần đầu truy cập: lưu lại phiên bản hiện tại, không reload
      localStorage.setItem(STORAGE_KEY, versionSignature);
      if (remoteVersion) {
        localStorage.setItem('app_display_version', remoteVersion);
      }
      isChecking = false;
      return false;
    }

    // Nếu version trên server khác version đã lưu trong browser
    if (currentVersionSignature !== versionSignature) {
      console.info(
        `[VersionCheck] Phát hiện phiên bản mới: ${remoteVersion || versionSignature}. Đang xóa cache và tải lại...`
      );
      if (remoteVersion) {
        localStorage.setItem('app_display_version', remoteVersion);
      }
      await clearCacheAndReload(versionSignature);
      return true;
    }
  } catch (err) {
    console.error('[VersionCheck] Kiểm tra phiên bản thất bại:', err);
  } finally {
    isChecking = false;
  }

  return false;
}

/**
 * Khởi động hệ thống tự động kiểm tra phiên bản mới
 */
export function initVersionCheck(options?: {
  intervalMs?: number; // Mặc định 5 phút (300000ms)
  checkOnFocus?: boolean; // Mặc định true (khi chuyển lại tab)
}) {
  // 1. Dọn sạch param `_reload` trên thanh URL nếu vừa mới reload xong
  if (window.location.search.includes('_reload=')) {
    try {
      const url = new URL(window.location.href);
      url.searchParams.delete('_reload');
      const cleanUrl = url.pathname + (url.search ? url.search : '') + url.hash;
      window.history.replaceState({}, document.title, cleanUrl);
    } catch (e) {
      // ignore
    }
  }

  // 2. Chạy kiểm tra ngay khi ứng dụng khởi động
  checkAppVersion();

  // 3. Định kỳ kiểm tra (mặc định mỗi 5 phút)
  const intervalMs = options?.intervalMs ?? 5 * 60 * 1000;
  if (intervalMs > 0) {
    setInterval(() => {
      checkAppVersion();
    }, intervalMs);
  }

  // 4. Kiểm tra khi người dùng quay lại tab (focus / visibilitychange)
  const checkOnFocus = options?.checkOnFocus ?? true;
  if (checkOnFocus && typeof document !== 'undefined') {
    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'visible') {
        checkAppVersion();
      }
    });
    window.addEventListener('focus', () => {
      checkAppVersion();
    });
  }
}
